import type { CSSProperties, ReactNode } from 'react';

// חלקי תצוגה משותפים לדשבורד (שפת GoCall v2): כותרת כרטיס עם נקודה, תא KPI
// עם מפריד, פס התקדמות דק, ופורמט כסף/אחוזים.

export const CARD_STYLE: CSSProperties = {
  position: 'relative',
  padding: 'clamp(18px, 4vw, 28px) clamp(16px, 4vw, 32px)',
};

export function SectionTitle({ title, sub, dot = 'var(--accent)', end }: { title: string; sub?: string; dot?: string; end?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '-.2px' }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block', width: 7, height: 7, borderRadius: 99, background: dot,
              marginInlineEnd: 9, verticalAlign: 'middle',
              boxShadow: `0 0 0 4px color-mix(in oklab, ${dot} 16%, transparent)`,
            }}
          />
          {title}
        </h3>
        {sub && <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{sub}</span>}
      </div>
      {end}
    </div>
  );
}

/** תא KPI: תווית קטנה, מספר גדול ודק, ושורת משנה (שינוי/הסבר). */
export function KpiCell({ label, value, foot, size = 36, color, flex = '1 1 120px', delay = 0 }: {
  label: ReactNode; value: ReactNode; foot?: ReactNode; size?: number; color?: string; flex?: string; delay?: number;
}) {
  return (
    <div className="dk-kpi" style={{ '--d': `${delay}s`, position: 'relative', flex, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 18px 12px', borderRadius: 12 } as CSSProperties}>
      <span aria-hidden="true" style={{ position: 'absolute', top: 8, bottom: 8, insetInlineStart: -1, width: 1, background: 'var(--glass-line-2)' }} />
      <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{label}</span>
      <span style={{ fontSize: size, fontWeight: 300, lineHeight: 1.05, letterSpacing: size >= 40 ? '-2px' : '-1.2px', fontVariantNumeric: 'tabular-nums', color: color ?? 'var(--text)' }}>{value}</span>
      {foot && <span style={{ fontSize: 12, minHeight: 16 }}>{foot}</span>}
    </div>
  );
}

/**
 * שינוי באחוזים מול אותה שעה אתמול. higherIsBad הופך את הצבע (למשל
 * "הוחמצו" — עלייה היא רעה).
 */
export function Delta({ value, higherIsBad = false, suffix = '' }: { value: number | null | undefined; higherIsBad?: boolean; suffix?: string }) {
  if (value == null || value === 0) return <span style={{ color: 'var(--muted)' }}>{value === 0 ? `ללא שינוי${suffix}` : ''}</span>;
  const good = higherIsBad ? value < 0 : value > 0;
  return (
    <span style={{ fontWeight: 600, color: good ? 'var(--ok)' : 'var(--danger)' }}>
      {value > 0 ? '▲' : '▼'} {Math.abs(value)}%{suffix}
    </span>
  );
}

/** פס התקדמות. הגדילה בכניסה מגיעה מבחוץ (pct כפול useEntrance), כמו animT בעיצוב. */
export function Bar({ pct, color = 'var(--accent)', height = 10 }: { pct: number; color?: string; height?: number }) {
  return (
    <div style={{ height, borderRadius: 99, background: 'var(--surface-3)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, borderRadius: 99, background: color, transition: 'width .3s ease' }} />
    </div>
  );
}

export function shekels(agorot: number): string {
  return `₪${Math.round(agorot / 100).toLocaleString('he-IL')}`;
}

export function pct(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 100) : null;
}
